/* Optional presentation enhancement. The saved rich text remains the source. */
(() => {
  'use strict';
  if (window.BPLProductSpecifications) {
    window.BPLProductSpecifications.init(document);
    return;
  }

  const scopeSelector = '.bpl-product-content--01';
  const detailSelector = 'details[data-bpl-dimensions]';
  const aliases = {
    'widest diameter': 'widest', 'widest insertable diameter': 'widest',
    'maximum diameter': 'maximum', 'maximum insertable diameter': 'maximum',
    diameter: 'diameter', 'insertable length': 'insertable', insertable: 'insertable',
    'overall length': 'overall', 'total length': 'overall',
    'neck diameter': 'neck', 'base diameter': 'base', circumference: 'circumference'
  };
  const labels = {
    widest: 'Widest diameter', maximum: 'Maximum diameter', diameter: 'Diameter',
    insertable: 'Insertable length', overall: 'Overall length',
    neck: 'Neck diameter', base: 'Base diameter', circumference: 'Circumference'
  };
  const order = Object.keys(labels);
  const number = '(?:\\d+\\s+\\d+\\/\\d+|\\d+\\/\\d+|\\d+(?:\\.\\d+)?)';
  const measure = new RegExp('^(' + number + '\\s*(?:inches|inch|in\\.?|cm|mm|["″]))(?:\\s+(.+))?$', 'iu');
  const safeInline = new Set(['SPAN', 'STRONG', 'B', 'EM', 'I', 'U', 'S', 'SMALL', 'SUB', 'SUP', 'A', 'BR']);
  const emphasisInline = new Set(['STRONG', 'B', 'EM', 'I', 'U', 'S', 'SMALL', 'SUB', 'SUP']);

  function keyFor(label) {
    return aliases[label.trim().replace(/\s+/gu, ' ').toLowerCase()] || null;
  }

  function parseField(text) {
    const colon = text.indexOf(':');
    if (colon > 0) {
      const key = keyFor(text.slice(0, colon));
      if (!key) return null;
      const value = text.slice(colon + 1).trim();
      const matched = value.match(measure);
      if (!matched || (matched[2] && keyFor(matched[2]) !== key)) return null;
      return { key, value: matched[1] };
    }
    const matched = text.trim().match(measure);
    if (!matched || !matched[2]) return null;
    const key = keyFor(matched[2]);
    return key ? { key, value: matched[1] } : null;
  }

  function parseLine(text) {
    text = text.trim();
    if (!text || text.length > 600 || /(?:https?:|\/\/|@)/iu.test(text)) return null;
    if (!text.includes('|')) {
      const field = parseField(text);
      if (field) return { kind: 'fields', fields: [field] };
    }
    const colon = text.indexOf(':');
    if (colon < 1) return null;
    const name = text.slice(0, colon).trim();
    if (keyFor(name)) return null;
    if (name.length > 72 || name.split(/\s+/u).length > 9 || !/\p{L}/u.test(name) || /[.!?;<>]/u.test(name)) return null;
    if (/^(?:note|notes|warning|warnings|care|material|materials|function|functions|use|features|included)$/iu.test(name)) return null;
    const parts = text.slice(colon + 1).split('|');
    if (parts.length > 7) return null;
    const fields = parts.map(part => parseField(part.trim()));
    if (fields.some(field => !field) || new Set(fields.map(field => field.key)).size !== fields.length) return null;
    fields.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
    return { kind: 'items', name, fields };
  }

  function sameGroup(rows) {
    if (rows.length < 2 || rows.some(row => !row || row.kind !== rows[0].kind)) return false;
    if (rows[0].kind === 'fields') return new Set(rows.map(row => row.fields[0].key)).size === rows.length;
    // Known fields can be absent without invalidating the whole comparison.
    // Each row still passes the strict numeric/unit parser; never infer a value.
    return new Set(rows.map(row => row.name.toLowerCase())).size === rows.length;
  }

  function columnsFor(rows) {
    return order.filter(key => rows.some(row => row.fields.some(field => field.key === key)));
  }

  function displayValue(value) {
    return value.replace(/\s*(?:inches|inch|in\.?|["″])$/iu, ' in');
  }

  function hasValueEmphasis(line) {
    const colon = line.text.indexOf(':');
    return line.emphasis.some(range => /\S/u.test(line.text.slice(Math.max(range.start, colon + 1), range.end)));
  }

  // Record DOM boundaries before altering anything. A link blocks only its own
  // line; untouched notes and links retain their actual nodes and listeners.
  function readLines(element) {
    if (element.id || element.hasAttribute('contenteditable')) return null;
    let text = '', blocked = false, valid = true, emphasis = [];
    let start = { node: element, offset: 0 };
    const lines = [];
    function finish(end, next) {
      lines.push({ text, blocked, emphasis, start, end });
      text = ''; blocked = false; emphasis = []; start = next;
    }
    function addText(value, formatted) {
      if (formatted && value.length) emphasis.push({ start: text.length, end: text.length + value.length });
      text += value;
    }
    function visit(node, interactive = false, formatted = false) {
      if (node.nodeType === 3) {
        const data = node.data;
        let from = 0;
        for (let at = 0; at < data.length; at += 1) {
          if (data[at] !== '\n') continue;
          addText(data.slice(from, at).replace(/\r$/u, ''), formatted);
          blocked = blocked || interactive;
          finish({ node, offset: at + 1 }, { node, offset: at + 1 });
          from = at + 1;
        }
        addText(data.slice(from), formatted);
        blocked = blocked || (interactive && data.length > from);
        return;
      }
      if (node.nodeType !== 1) { valid = false; return; }
      if (!safeInline.has(node.tagName) || node.hasAttribute('contenteditable') || node.id) { valid = false; return; }
      if (node.tagName === 'BR') {
        const parent = node.parentNode;
        const offset = Array.prototype.indexOf.call(parent.childNodes, node) + 1;
        finish({ node: parent, offset }, { node: parent, offset });
        return;
      }
      Array.from(node.childNodes).forEach(child => visit(child, interactive || node.tagName === 'A', formatted || emphasisInline.has(node.tagName)));
    }
    Array.from(element.childNodes).forEach(node => visit(node));
    finish({ node: element, offset: element.childNodes.length }, null);
    return valid ? lines : null;
  }

  function appendElement(parent, tag, text, attributes = {}) {
    const node = parent.ownerDocument.createElement(tag);
    if (text !== null) node.textContent = text;
    Object.entries(attributes).forEach(([name, value]) => node.setAttribute(name, value));
    parent.appendChild(node);
    return node;
  }

  function makeTable(document, rows) {
    const table = document.createElement('table');
    table.className = 'bpl-specifications';
    table.setAttribute('role', 'table');
    table.setAttribute('data-bpl-specifications', '');
    const items = rows[0].kind === 'items';
    const columns = items ? columnsFor(rows) : [];
    const standardSizes = items && rows.every(row => /^(?:extra[ -]small|small|medium|large|extra[ -]large|xxs|xs|s|m|l|xl|xxl)(?: plug)?$/iu.test(row.name));
    if (!items) table.classList.add('bpl-specifications--single');
    appendElement(table, 'caption', items ? 'Size comparison' : 'Product dimensions', { class: 'bpl-specifications__caption' });
    if (items) {
      const head = appendElement(table, 'thead', null, { role: 'rowgroup' });
      const heading = appendElement(head, 'tr', null, { role: 'row' });
      appendElement(heading, 'th', standardSizes ? 'Size' : 'Item', { scope: 'col', role: 'columnheader' });
      columns.forEach(key => appendElement(heading, 'th', labels[key], { scope: 'col', role: 'columnheader' }));
    }
    const body = appendElement(table, 'tbody', null, { role: 'rowgroup' });
    const sorted = items ? rows : [...rows].sort((a, b) => order.indexOf(a.fields[0].key) - order.indexOf(b.fields[0].key));
    sorted.forEach(row => {
      const tr = appendElement(body, 'tr', null, { role: 'row' });
      const name = items && standardSizes ? row.name.replace(/ plug$/iu, '') : row.name;
      appendElement(tr, 'th', items ? name : labels[row.fields[0].key], { scope: 'row', role: 'rowheader' });
      const cells = items ? columns.map(key => row.fields.find(field => field.key === key) || { key, value: null }) : row.fields;
      cells.forEach(field => {
        const td = appendElement(tr, 'td', null, { role: 'cell' });
        if (items) appendElement(td, 'span', labels[field.key], { class: 'bpl-specifications__mobile-label', 'aria-hidden': 'true' });
        const value = appendElement(td, 'span', null, { class: 'bpl-specifications__value' });
        if (field.value === null) {
          appendElement(value, 'span', '—', { 'aria-hidden': 'true' });
          appendElement(value, 'span', 'Not specified', { class: 'bpl-specifications__sr-only' });
        } else value.textContent = displayValue(field.value);
      });
    });
    return table;
  }

  function enhanceParagraph(element) {
    // Tables cannot be inserted inside P or LI. A multiline P is enhanced only
    // at its beginning, leaving every trailing note in its original paragraph.
    if (element.tagName !== 'P') return;
    const lines = readLines(element);
    if (!lines) return;
    const rows = [];
    let end = null;
    for (const line of lines) {
      if (!line.text.trim()) { if (rows.length) end = line.end; continue; }
      // A label can be bold already. Value emphasis must retain its actual
      // merchant markup instead of silently becoming an unformatted table cell.
      const row = line.blocked || hasValueEmphasis(line) ? null : parseLine(line.text);
      if (!row) {
        // An apparent third specification that failed parsing is not a note:
        // leave the whole candidate group alone instead of comparing a subset.
        if (looksLikeSpecification(line.text)) return;
        break;
      }
      rows.push(row); end = line.end;
    }
    if (!sameGroup(rows)) return;
    const range = element.ownerDocument.createRange();
    range.setStart(element, 0);
    range.setEnd(end.node, end.offset);
    const table = makeTable(element.ownerDocument, rows);
    element.parentNode.insertBefore(table, element);
    range.deleteContents();
    if (!element.textContent.trim() && !element.querySelector('a, img, video, iframe')) element.remove();
  }

  function nextElement(element) {
    let node = element.nextSibling;
    while (node && node.nodeType === 3 && !node.textContent.trim()) node = node.nextSibling;
    return node && node.nodeType === 1 ? node : null;
  }

  function looksLikeSpecification(text) {
    const colon = text.indexOf(':');
    const name = colon > 0 ? text.slice(0, colon).trim() : '';
    if (text.includes('|') || keyFor(name)) return true;
    // A rejected one-field size row is still part of the candidate comparison.
    // Do not mistake it for a trailing note and silently show only earlier sizes.
    if (/^(?:extra[ -]small|small|medium|large|extra[ -]large|xxs|xs|s|m|l|xl|xxl)(?:\s|$)/iu.test(name)) return true;
    if (!name || /^(?:note|notes|warning|warnings|care|material|materials|function|functions|use|features|included)$/iu.test(name)) return false;
    return /\d\s*(?:inches\b|inch\b|in\b|cm\b|mm\b|["″])/iu.test(text.slice(colon + 1));
  }

  function previousElement(element) {
    let node = element.previousSibling;
    while (node && node.nodeType === 3 && !node.textContent.trim()) node = node.previousSibling;
    return node && node.nodeType === 1 ? node : null;
  }

  function wholeRow(element) {
    if (!element || !['P', 'LI'].includes(element.tagName)) return null;
    const lines = readLines(element);
    if (!lines) return null;
    const nonempty = lines.filter(line => line.text.trim());
    if (nonempty.length !== 1 || nonempty[0].blocked || hasValueEmphasis(nonempty[0])) return null;
    return parseLine(nonempty[0].text);
  }

  function enhanceSiblings(first) {
    const row = wholeRow(first);
    if (!row) return;
    const previous = previousElement(first);
    if (previous && previous.tagName === first.tagName && (wholeRow(previous) || looksLikeSpecification(previous.textContent))) return;
    const list = first.tagName === 'LI' ? first.parentNode : null;
    if (list && (!['UL', 'OL'].includes(list.tagName) || list.firstElementChild !== first)) return;
    const elements = [first], rows = [row];
    let next = nextElement(first);
    while (next && next.tagName === first.tagName) {
      const candidate = wholeRow(next);
      if (!candidate) {
        if (looksLikeSpecification(next.textContent)) return;
        break;
      }
      // A consecutive but incompatible specification stays wholly in its
      // original format instead of becoming a misleading partial comparison.
      rows.push(candidate); elements.push(next); next = nextElement(next);
    }
    if (!sameGroup(rows)) return;
    const table = makeTable(first.ownerDocument, rows);
    const anchor = list || first;
    anchor.parentNode.insertBefore(table, anchor);
    elements.forEach(element => element.remove());
    if (list && !list.children.length && !list.textContent.trim()) list.remove();
  }

  function enhance(details) {
    if (!details.closest(scopeSelector) || details.closest('a, pre, code, form, [contenteditable]')) return;
    const content = details.querySelector('.bpl-structured-detail__content');
    if (!content) return;
    Array.from(content.querySelectorAll('p')).forEach(element => {
      if (!element.closest('table, li, blockquote')) enhanceParagraph(element);
    });
    Array.from(content.querySelectorAll('p, li')).forEach(element => {
      if (content.contains(element) && !element.closest('table, blockquote') && !(element.tagName === 'P' && element.closest('li'))) enhanceSiblings(element);
    });
  }

  function init(root = document) {
    const details = [];
    if (root.matches && root.matches(detailSelector)) details.push(root);
    if (root.querySelectorAll) details.push(...root.querySelectorAll(detailSelector));
    details.forEach(enhance);
  }

  window.BPLProductSpecifications = { init };
  document.addEventListener('shopify:section:load', event => init(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
})();
